import { useState, useRef } from 'react'
import { Upload, X } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

export default function ArchiveUpload({ onComplete, setStatus }) {
  const { apiKey } = useAuth()
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef(null)
  const xhrRef = useRef(null)

  function handleFiles(fileList) {
    const file = fileList[0]
    if (!file) return

    // Validate
    if (!file.name.match(/\.(mp4|mov|mkv|avi|webm|m4v)$/i)) {
      setStatus('Only video files are accepted')
      return
    }

    startUpload(file)
  }

  async function startUpload(file) {
    setUploading(true)
    setProgress(0)

    // Use the video filename as R2 key, ensure .mp4 extension
    let filename = file.name
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '-')
      .replace(/\s+/g, ' ')
      .trim()

    // Swap extension to .mp4 for consistency
    filename = filename.replace(/\.(mov|mkv|avi|webm|m4v)$/i, '.mp4')
    if (!filename.toLowerCase().endsWith('.mp4')) {
      filename += '.mp4'
    }

    setStatus(`Uploading "${filename}"...`)

    try {
      const API_BASE = import.meta.env.VITE_API_BASE || '/api'

      const xhr = new XMLHttpRequest()
      xhrRef.current = xhr

      const formData = new FormData()
      formData.append('file', file)
      formData.append('filename', filename)

      await new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', e => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100))
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText}`))
          }
        })

        xhr.addEventListener('error', () => reject(new Error('Upload failed')))
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')))

        xhr.open('POST', `${API_BASE}/archive/upload`)
        xhr.setRequestHeader('X-API-Key', apiKey)
        xhr.send(formData)
      })

      setStatus(`✅ "${filename}" uploaded to archive`)
      onComplete()
    } catch (e) {
      if (e.message !== 'Upload cancelled') {
        setStatus(`Error: ${e.message}`)
      }
    } finally {
      setUploading(false)
      setProgress(0)
      xhrRef.current = null
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  function handleCancel() {
    if (xhrRef.current) {
      xhrRef.current.abort()
      setStatus('Upload cancelled')
    }
    setUploading(false)
    setProgress(0)
  }

  function handleDrop(e) {
    e.preventDefault()
    setDragOver(false)
    handleFiles(e.dataTransfer.files)
  }

  return (
    <div className="archive-upload-section">
      <h4>
        <Upload size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
        Upload to Archive
      </h4>

      {uploading ? (
        <div className="archive-upload-progress">
          <div className="progress-bar-track">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="progress-info">
            <span>{progress}%</span>
            <button onClick={handleCancel} className="cancel-btn">
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`archive-drop-zone ${dragOver ? 'drag-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
        >
          <Upload size={24} style={{ opacity: 0.4, marginBottom: 8 }} />
          <p>Drop a video file here or click to browse</p>
          <span className="muted" style={{ fontSize: 11 }}>MP4, MOV, MKV, AVI, WebM</span>
          <input
            ref={fileRef}
            type="file"
            accept="video/*"
            onChange={e => handleFiles(e.target.files)}
            style={{ display: 'none' }}
          />
        </div>
      )}
    </div>
  )
}
